import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Copy, Sparkles, Users, DollarSign, Gift, Trophy, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Ambassador() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) api.get("/ambassador/me").then((r) => setData(r.data)).catch(() => {});
  }, [user]);

  const join = async () => {
    await api.post("/ambassador/join");
    await refresh();
    toast.success("¡Ya eres Embajador Drelo! 🎉");
    api.get("/ambassador/me").then((r) => setData(r.data));
  };

  const copy = () => {
    const link = `${window.location.origin}/entrar?ref=${user.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("¡Enlace copiado! Compártelo y gana 💰");
    setTimeout(() => setCopied(false), 1500);
  };

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10">
        <Hero />
        <div className="text-center mt-8 bg-white border rounded-2xl p-8">
          <h2 className="font-display font-black text-2xl uppercase">Inicia sesión para empezar a ganar</h2>
          <p className="text-drelo-muted mt-2">Crea tu cuenta gratis y obtén tu código de embajador al instante.</p>
          <button onClick={() => nav("/entrar")} className="mt-5 bg-drelo-primary text-white font-black uppercase px-8 py-3.5 rounded-full hover:bg-drelo-hover" data-testid="ambassador-login-cta">Crear cuenta gratis</button>
        </div>
      </div>
    );
  }

  const tiers = data?.tiers || [];
  const sales = data?.user?.total_referral_sales || 0;
  const prog = data?.progress_to_next;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <Hero />

      {!user.is_ambassador ? (
        <div className="text-center mt-8 bg-white border rounded-2xl p-8">
          <h2 className="font-display font-black text-2xl uppercase">Activa tu cuenta de Embajador</h2>
          <p className="text-drelo-muted mt-2">Un clic y empiezas a ganar 10% por cada venta con tu código.</p>
          <button onClick={join} className="mt-5 bg-drelo-primary text-white font-black uppercase px-8 py-3.5 rounded-full hover:bg-drelo-hover" data-testid="join-ambassador-btn">Ser Embajador gratis</button>
        </div>
      ) : (
        <>
          {/* Code + stats */}
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            <div className="md:col-span-1 bg-gradient-to-br from-drelo-ink to-[#1e293b] text-white rounded-2xl p-6">
              <p className="text-xs uppercase tracking-widest text-white/50 font-bold">Tu código</p>
              <p className="font-display font-black text-3xl mt-1" data-testid="referral-code">{user.referral_code}</p>
              <button onClick={copy} className="mt-4 w-full bg-drelo-primary text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-drelo-hover" data-testid="copy-referral-btn">
                {copied ? <><Check className="w-4 h-4" /> Copiado</> : <><Share2 className="w-4 h-4" /> Copiar enlace</>}
              </button>
            </div>
            <div className="md:col-span-2 grid grid-cols-3 gap-3">
              {[
                [DollarSign, `$${(data?.user?.credits || 0).toFixed(2)}`, "Créditos ganados", "credits-stat"],
                [Users, data?.user?.referrals_count || 0, "Ventas referidas", "referrals-stat"],
                [Gift, `$${sales.toFixed(2)}`, "Total generado", "sales-stat"],
              ].map(([Icon, v, l, tid], i) => (
                <div key={i} className="bg-white border rounded-2xl p-4 text-center flex flex-col justify-center" data-testid={tid}>
                  <Icon className="w-6 h-6 mx-auto text-drelo-primary mb-1" />
                  <div className="font-display font-black text-2xl">{v}</div>
                  <div className="text-xs text-drelo-muted">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Progress */}
          {prog && data?.next_tier && (
            <div className="bg-white border rounded-2xl p-5 mt-4">
              <div className="flex justify-between text-sm font-bold mb-2">
                <span>Nivel actual: <span className="text-drelo-primary">{data.current_tier.name}</span></span>
                <span>Siguiente: {data.next_tier.name}</span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-drelo-primary to-drelo-deal transition-all" style={{ width: `${Math.min(100, (prog[0] / prog[1]) * 100)}%` }} />
              </div>
              <p className="text-xs text-drelo-muted mt-2">Te faltan <b className="text-drelo-ink">${(prog[1] - prog[0]).toFixed(2)}</b> en ventas para desbloquear <b>{data.next_tier.reward}</b></p>
            </div>
          )}
        </>
      )}

      {/* Tiers / volume gifts */}
      <h2 className="font-display font-black text-2xl uppercase tracking-tight mt-10 mb-4 flex items-center gap-2"><Trophy className="w-6 h-6 text-drelo-deal" /> Niveles y comisiones</h2>
      <div className="grid md:grid-cols-4 gap-4">
        {(tiers.length ? tiers : DEFAULT_TIERS).map((t, i) => {
          const unlocked = sales >= t.min_sales;
          return (
            <div key={i} className={`rounded-2xl p-5 border-2 ${unlocked ? "border-drelo-mint bg-green-50" : "border-slate-100 bg-white"}`}>
              <div className="flex items-center justify-between">
                <span className="font-display font-black text-lg">{t.name}</span>
                {unlocked && <Check className="w-5 h-5 text-drelo-mint" />}
              </div>
              <p className="text-xs text-drelo-muted mt-1">Desde ${t.min_sales} en ventas</p>
              <p className="text-sm font-semibold mt-3">{t.reward}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const DEFAULT_TIERS = [
  { name: "Bronce", min_sales: 0, reward: "10% comisión por venta" },
  { name: "Plata", min_sales: 250, reward: "12% comisión por venta" },
  { name: "Oro", min_sales: 750, reward: "15% comisión por venta" },
  { name: "Diamante", min_sales: 2000, reward: "20% comisión por venta" },
];

function Hero() {
  return (
    <div className="rounded-2xl bg-gradient-to-r from-drelo-primary to-drelo-deal text-white p-8 md:p-12 relative overflow-hidden">
      <Sparkles className="absolute right-6 top-6 w-28 h-28 opacity-20" />
      <span className="text-xs font-black uppercase tracking-widest bg-white/20 px-3 py-1 rounded-full">Programa de Embajadores</span>
      <h1 className="font-display font-black text-3xl md:text-5xl uppercase tracking-tight mt-3 leading-[0.95]">Gana dinero<br />compartiendo Drelo</h1>
      <p className="mt-3 text-white/90 max-w-xl">10% de comisión por cada venta, regalos por volumen y niveles que suben tus recompensas. Sin costo, sin límites.</p>
    </div>
  );
}
